/**
 * YouTubePage (was: PublishPage)
 *
 * Unified YouTube page. Two top sub-tabs, one sidebar entry:
 *
 *  1. "Download"              — yt-dlp downloader (desktop-only)
 *  2. "Generate SEO + Images" — one surface with an Instagram | YouTube switch:
 *       - Instagram: posts/carousel studio (PostsTab) — caption + images.
 *       - YouTube:   OAuth + SEO + publish (YouTubeUploadTab) — publish-from-
 *                    generation OR edit-existing; the LLM writes the SEO.
 *
 * The old standalone "Publish to YouTube" tab was folded into the YouTube
 * destination here (RULE #-2: one SEO+publish surface, no duplicate UIs).
 */

import type { LucideIcon } from 'lucide-react';
import { Download, Sparkles, Youtube, Instagram } from 'lucide-react';
import { SubTabBar, type SubTabItem } from '@/components/common';
import { useTranslation } from '../../hooks/useTranslation';
import { isServerlessMode } from '../../utils/apiMode';
import { PublishProvider } from './PublishContext';
import { usePublishContext, type PublishTab, type MediaTab } from './PublishContext.context';
import { PostsTab } from '../../components/image/tabs/PostsTab';
import { YouTubeUploadTab } from './tabs/YouTubeUploadTab';
import { YouTubeDownloader } from '../../components/youtube/YouTubeDownloader';

const TABS: Array<{
  id: PublishTab;
  labelKey: string;
  defaultLabel: string;
  icon: LucideIcon;
}> = [
  { id: 'download', labelKey: 'youtube.tab.download', defaultLabel: 'Download', icon: Download },
  {
    id: 'generate',
    labelKey: 'youtube.tab.generate',
    defaultLabel: 'Generate SEO + Images',
    icon: Sparkles,
  },
];

const MEDIA_TABS: Array<{
  id: MediaTab;
  labelKey: string;
  defaultLabel: string;
  icon: LucideIcon;
}> = [
  {
    id: 'youtube',
    labelKey: 'youtube.mediaTab.youtube',
    defaultLabel: 'YouTube',
    icon: Youtube,
  },
  {
    id: 'instagram',
    labelKey: 'youtube.mediaTab.instagram',
    defaultLabel: 'Instagram',
    icon: Instagram,
  },
];

function YouTubePageInner() {
  const { tab, setTab, mediaTab, setMediaTab } = usePublishContext();
  const { t } = useTranslation();

  // The 'Download' sub-tab wraps yt-dlp, which YouTube IP-bans from Cloud Run.
  // On web, hide it (Generate SEO + Publish use the Web OAuth flow); on desktop
  // it stays visible. SubTabBar respects `hidden`, so the behavior is identical.
  const items: SubTabItem[] = TABS.map(({ id, labelKey, defaultLabel, icon }) => ({
    id,
    label: t(labelKey, { defaultValue: defaultLabel }),
    icon,
    hidden: id === 'download' ? isServerlessMode() : false,
    testId: `youtube-page-tab-${id}`,
  }));

  const mediaItems: SubTabItem[] = MEDIA_TABS.map(({ id, labelKey, defaultLabel, icon }) => ({
    id,
    label: t(labelKey, { defaultValue: defaultLabel }),
    icon,
    testId: `youtube-media-tab-${id}`,
  }));

  return (
    <div className="flex flex-col h-full">
      {/* Top tab bar */}
      <SubTabBar
        tabs={items}
        activeId={tab}
        onChange={(id) => setTab(id as PublishTab)}
        accent="purple"
      />

      {/* Tab content */}
      <div className="flex-1 overflow-auto">
        {tab === 'download' && (
          <main className="w-full max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
            <YouTubeDownloader />
          </main>
        )}
        {tab === 'generate' && (
          <div className="flex flex-col h-full">
            {/* Destination switch: Instagram vs YouTube */}
            <SubTabBar
              tabs={mediaItems}
              activeId={mediaTab}
              onChange={(id) => setMediaTab(id as MediaTab)}
              accent="purple"
            />
            <div className="flex-1 overflow-auto">
              {mediaTab === 'youtube' && <YouTubeUploadTab />}
              {mediaTab === 'instagram' && (
                <div className="w-full max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
                  <PostsTab />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function YouTubePage() {
  return (
    <PublishProvider>
      <YouTubePageInner />
    </PublishProvider>
  );
}

/** @deprecated Use YouTubePage — kept only until all importers are updated. */
export { YouTubePage as PublishPage };
